/**
 * The throw (owner: ball/passing team), called by the controller on THROW_RELEASE's last startup frame.
 * Launches the ball from the glove's release point with (speed, angle) from PASS_CURVE (a raceable jump ball for
 * the thrower to chase) or, for FG / PAT tosses, THROW_CURVE (enough carry for the posts) at the charge fraction,
 * ARM (or TOSS for FG/PAT formations) and Hail Mary multipliers, the lob bonus, and part of the thrower's own
 * velocity. Sets the rules' throw fields (rules/helpers.ts protocol): rules.passThrown, ball.forwardPass (QB mode
 * at release), thrownBy, releaseX, fgAttempt (forwardPass && playCall ∈ {FG_TOSS, PAT_TOSS}).
 * Spin: a pass is a tight spiral whose nose follows the arc; a toss tumbles end over end like a kick.
 */
import {
  KICKOFF_CURVE,
  MOTION,
  ONSIDE_CURVE,
  PHYSICS,
  PUNT_CURVE,
  SPECIALS_TUNING,
  SpecialId,
  THROW,
  PI,
  datan2Into,
  dsinCosInto,
  sampleCurve,
  samplePassCurve,
  sampleThrowCurve,
} from '@hf/shared';
import { BallState, BtnIndex, PlayCall, SpecialPhase, isKickCall } from '../enums';
import { throwChargeFrac } from '../player/frames';
import { isHeld } from '../player/input';
import { ballBeyondLos, isQbMode } from '../selectors';
import type { SimEventSink } from '../sink';
import type { PlayerSim, SimState } from '../state';

const curve = { speed: 0.5, angle: 0.5 };
const trig = { c: 0.5, s: 0.5 };
const heading = { v: 0.5 };

/** Is the next throw of `p` a Hail Mary (special ACTIVE and armed until the throw)? */
export const hailMaryArmed = (p: PlayerSim): boolean =>
  p.special.id === SpecialId.HAIL_MARY && p.special.phase === SpecialPhase.ACTIVE;

const KICK_KINDS = ['kickoff', 'onside', 'punt'] as const;

/**
 * Launch the ball held by `p`. Returns false (nothing happens) if `p` may not throw now (not in QB mode).
 * Classic kick plays (rules.playCall KICKOFF / PUNT): the toss is a KICK (`ball.kick`, KICKOFF_CURVE /
 * PUNT_CURVE × TOSS, tumbling; THROW + KICK events); a kickoff released with ▼ held is the ONSIDE hop (the rules'
 * playCall becomes ONSIDE). `auto` = kicked by the rules (play clock / KICK.kickoffAutoFrames): never onside.
 */
export const launchBall = (s: SimState, ev: SimEventSink, p: PlayerSim, auto = false): boolean => {
  const b = s.ball;
  const r = s.rules;
  if (b.state !== BallState.HELD || b.holder !== p.index || !isQbMode(s, p.index)) return false;
  const dir = r.attackDir;
  const charge = throwChargeFrac(p);
  const kick = isKickCall(r.playCall);
  if (kick && r.playCall === PlayCall.KICKOFF && !auto && isHeld(p.input, BtnIndex.D))
    r.playCall = PlayCall.ONSIDE;
  const toss = r.playCall === PlayCall.FG_TOSS || r.playCall === PlayCall.PAT_TOSS;
  if (kick)
    sampleCurve(
      r.playCall === PlayCall.PUNT
        ? PUNT_CURVE
        : r.playCall === PlayCall.ONSIDE
          ? ONSIDE_CURVE
          : KICKOFF_CURVE,
      charge,
      curve,
    );
  else if (toss) sampleThrowCurve(charge, curve);
  else samplePassCurve(charge, curve);
  const hm = !kick && hailMaryArmed(p);
  const speed =
    curve.speed *
    ((toss || kick) && THROW.tossUsesTossMul ? p.stats.tossMul : p.stats.armMul) *
    (hm ? SPECIALS_TUNING.hailMary.speedMul : 1);
  const angle = curve.angle + (p.lob ? THROW.lobBonusAngle : 0);
  dsinCosInto(trig, angle);
  const vx = dir * speed * trig.c + p.vx * MOTION.throwInheritVel;
  const vy = speed * trig.s + p.vy * MOTION.throwInheritVel;

  b.state = BallState.AIR;
  b.holder = -1;
  b.x = p.x + dir * THROW.releaseOx;
  b.y = p.y + THROW.releaseOy;
  b.vx = vx;
  b.vy = vy;
  datan2Into(heading, vy, vx);
  b.angle = heading.v - PI * Math.round(heading.v / PI); // wrapHalfTurn, inline (nothing boxed)
  b.angVel = toss || kick ? -dir * PHYSICS.ballSpinPerSpeed * speed : 0;
  b.thrownBy = p.index;
  b.airFrames = 0;
  b.releaseX = b.x;
  b.forwardPass = true;
  b.passedLos = ballBeyondLos(s);
  b.fgAttempt = toss;
  b.hailMary = hm;
  b.touchedMask = 0;
  b.lastTouchedBy = p.index;
  b.bounces = 0;
  b.kick = kick;
  b.looseFrames = 0;
  r.passThrown = true;
  if (ev)
    ev.push({
      type: 'THROW',
      tick: s.tick,
      actor: p.index,
      x: b.x,
      y: b.y,
      vx,
      vy,
      charge,
      lob: p.lob,
      hailMary: hm,
      forwardPass: true,
    });
  if (kick && ev) {
    const kind = r.playCall === PlayCall.PUNT ? 2 : r.playCall === PlayCall.ONSIDE ? 1 : 0;
    ev.push({
      type: 'KICK',
      tick: s.tick,
      actor: p.index,
      x: b.x,
      y: b.y,
      vx,
      vy,
      kind: KICK_KINDS[kind]!,
      charge,
      auto,
    });
  }
  return true;
};
